/**
 * useWorkspaces Hook
 * Local workspace management for organizing boards
 * 
 * @module hooks/useWorkspaces
 */

import { useState, useEffect, useCallback } from 'react';
import { Workspace } from '../types';

const STORAGE_KEY = 'notesscape_workspaces';

// Default workspace colors
const WORKSPACE_COLORS = [
    '#3b82f6', // blue
    '#10b981', // green
    '#f59e0b', // amber
    '#ef4444', // red
    '#a855f7', // purple
    '#ec4899', // pink
    '#06b6d4', // cyan
    '#64748b', // slate
];

/**
 * Generate a unique ID
 */
const generateId = () => `ws_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

/**
 * Hook for managing workspaces with localStorage persistence
 */
export function useWorkspaces() {
    const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
    const [activeWorkspaceId, setActiveWorkspaceId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    // Load workspaces from localStorage
    useEffect(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const parsed = JSON.parse(stored) as Workspace[];
                setWorkspaces(parsed);
            } else {
                // Create default "All Boards" workspace
                const defaultWorkspace: Workspace = {
                    id: 'all',
                    name: 'All Boards',
                    color: WORKSPACE_COLORS[0],
                    boardIds: [],
                    isDefault: true,
                    createdAt: Date.now(),
                    order: 0,
                };
                setWorkspaces([defaultWorkspace]);
                localStorage.setItem(STORAGE_KEY, JSON.stringify([defaultWorkspace]));
            }
        } catch (error) {
            console.error('Failed to load workspaces:', error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Save workspaces to localStorage whenever they change
    useEffect(() => {
        if (!isLoading) {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(workspaces));
        }
    }, [workspaces, isLoading]);

    /**
     * Create a new workspace
     */
    const createWorkspace = useCallback((name: string, color?: string): Workspace => {
        const newWorkspace: Workspace = {
            id: generateId(),
            name,
            color: color || WORKSPACE_COLORS[workspaces.length % WORKSPACE_COLORS.length],
            boardIds: [],
            createdAt: Date.now(),
            order: workspaces.length,
        };
        setWorkspaces(prev => [...prev, newWorkspace]);
        return newWorkspace;
    }, [workspaces.length]);

    /**
     * Delete a workspace (cannot delete default)
     */
    const deleteWorkspace = useCallback((workspaceId: string) => {
        setWorkspaces(prev => {
            const workspace = prev.find(w => w.id === workspaceId);
            if (workspace?.isDefault) {
                console.warn('Cannot delete default workspace');
                return prev;
            }
            // Move boards back to default workspace
            const defaultWs = prev.find(w => w.isDefault);
            if (defaultWs && workspace) {
                return prev
                    .filter(w => w.id !== workspaceId)
                    .map(w => w.isDefault
                        ? { ...w, boardIds: [...w.boardIds, ...workspace.boardIds] }
                        : w
                    );
            }
            return prev.filter(w => w.id !== workspaceId);
        });
        // Reset active workspace if deleted
        if (activeWorkspaceId === workspaceId) {
            setActiveWorkspaceId(null);
        }
    }, [activeWorkspaceId]);

    /**
     * Rename a workspace
     */
    const renameWorkspace = useCallback((workspaceId: string, name: string) => {
        setWorkspaces(prev =>
            prev.map(w => w.id === workspaceId ? { ...w, name } : w)
        );
    }, []);

    /**
     * Update workspace color
     */
    const setWorkspaceColor = useCallback((workspaceId: string, color: string) => {
        setWorkspaces(prev =>
            prev.map(w => w.id === workspaceId ? { ...w, color } : w)
        );
    }, []);

    /**
     * Add a board to a workspace
     */
    const addBoardToWorkspace = useCallback((workspaceId: string, boardId: string) => {
        setWorkspaces(prev => {
            // First remove board from any other workspace
            const updated = prev.map(w => ({
                ...w,
                boardIds: w.boardIds.filter(id => id !== boardId)
            }));
            // Then add to target workspace
            return updated.map(w =>
                w.id === workspaceId
                    ? { ...w, boardIds: [...w.boardIds, boardId] }
                    : w
            );
        });
    }, []);

    /**
     * Remove a board from a workspace
     */
    const removeBoardFromWorkspace = useCallback((workspaceId: string, boardId: string) => {
        setWorkspaces(prev =>
            prev.map(w =>
                w.id === workspaceId
                    ? { ...w, boardIds: w.boardIds.filter(id => id !== boardId) }
                    : w
            )
        );
    }, []);

    /**
     * Get workspace for a specific board
     */
    const getWorkspaceForBoard = useCallback((boardId: string): Workspace | undefined => {
        return workspaces.find(w => w.boardIds.includes(boardId));
    }, [workspaces]);

    /**
     * Get boards in a workspace (or all boards if null/default)
     */
    const getBoardsInWorkspace = useCallback((workspaceId: string | null): string[] => {
        if (!workspaceId) return [];
        const workspace = workspaces.find(w => w.id === workspaceId);
        if (workspace?.isDefault) {
            // Default workspace shows all boards
            return [];
        }
        return workspace?.boardIds || [];
    }, [workspaces]);

    /**
     * Reorder workspaces
     */
    const reorderWorkspaces = useCallback((newOrder: string[]) => {
        setWorkspaces(prev => {
            const sorted = [...prev].sort((a, b) => {
                const indexA = newOrder.indexOf(a.id);
                const indexB = newOrder.indexOf(b.id);
                return indexA - indexB;
            });
            return sorted.map((w, index) => ({ ...w, order: index }));
        });
    }, []);

    return {
        workspaces,
        activeWorkspaceId,
        setActiveWorkspaceId,
        isLoading,
        createWorkspace,
        deleteWorkspace,
        renameWorkspace,
        setWorkspaceColor,
        addBoardToWorkspace,
        removeBoardFromWorkspace,
        getWorkspaceForBoard,
        getBoardsInWorkspace,
        reorderWorkspaces,
        WORKSPACE_COLORS,
    };
}

export default useWorkspaces;
